import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { User, Heart, Bell, Crown, Settings, LogOut } from 'lucide-react';
import { getWishlist, getPriceAlerts } from '../api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';

const Dashboard = () => {
  const { user, logout } = useAuth();
  const [wishlistCount, setWishlistCount] = useState(0);
  const [alertsCount, setAlertsCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [wishlistRes, alertsRes] = await Promise.all([
          getWishlist(),
          getPriceAlerts()
        ]);
        setWishlistCount(wishlistRes.data.length);
        setAlertsCount(alertsRes.data.length);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    if (user) fetchData();
  }, [user]);

  if (!user) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
        <div className="text-center">
          <User size={64} className="text-white/20 mx-auto mb-6" />
          <h1 className="font-display text-4xl uppercase mb-4">DASHBOARD</h1>
          <p className="text-white/60 mb-8">Sign in to view your dashboard</p>
          <Link to="/login">
            <Button className="btn-primary">SIGN IN</Button>
          </Link>
        </div>
      </div>
    );
  }

  const stats = [
    { label: 'Wishlist Items', value: wishlistCount, icon: Heart, href: '/wishlist', color: '#FF3B30' },
    { label: 'Price Alerts', value: alertsCount, icon: Bell, href: '/alerts', color: '#D2FF00' },
  ];

  const menuItems = [
    { label: 'My Wishlist', icon: Heart, href: '/wishlist' },
    { label: 'Price Alerts', icon: Bell, href: '/alerts' },
    { label: 'Premium', icon: Crown, href: '/premium', highlight: !user.is_premium },
  ];

  return (
    <div className="min-h-screen bg-[#050505] py-12 px-6 lg:px-12" data-testid="dashboard-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <div className="flex items-center gap-6 mb-8">
            <div className="w-20 h-20 bg-[#0A0A0A] border border-white/10 flex items-center justify-center">
              <User size={32} className="text-white/60" />
            </div>
            <div>
              <h1 className="font-display text-3xl uppercase">{user.name}</h1>
              <p className="text-white/60">{user.email}</p>
              {user.is_premium && (
                <span className="inline-flex items-center gap-1 text-[#D2FF00] text-sm mt-1">
                  <Crown size={14} />
                  Premium Member
                </span>
              )}
            </div>
          </div>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12"
        >
          {stats.map((stat) => (
            <Link key={stat.label} to={stat.href}>
              <div className="bg-[#0A0A0A] p-6 border border-white/10 hover:border-white/20 transition-colors">
                <div className="flex items-center justify-between mb-4">
                  <stat.icon size={24} style={{ color: stat.color }} />
                  <span className="font-display text-4xl">{loading ? '...' : stat.value}</span>
                </div>
                <p className="text-white/60 text-sm">{stat.label}</p>
              </div>
            </Link>
          ))}
        </motion.div>

        {/* Menu */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-2"
        >
          {menuItems.map((item) => (
            <Link key={item.label} to={item.href}>
              <div className={`flex items-center justify-between p-4 bg-[#0A0A0A] border hover:border-white/20 transition-colors ${
                item.highlight ? 'border-[#D2FF00]/50' : 'border-white/10'
              }`}>
                <div className="flex items-center gap-4">
                  <item.icon size={20} className={item.highlight ? 'text-[#D2FF00]' : 'text-white/60'} />
                  <span className={item.highlight ? 'text-[#D2FF00]' : ''}>{item.label}</span>
                </div>
                {item.highlight && !user.is_premium && (
                  <span className="text-xs bg-[#D2FF00] text-black px-2 py-1">UPGRADE</span>
                )}
              </div>
            </Link>
          ))}

          <button
            onClick={logout}
            className="w-full flex items-center gap-4 p-4 bg-[#0A0A0A] border border-white/10 hover:border-[#FF3B30]/50 hover:text-[#FF3B30] transition-colors"
            data-testid="logout-btn"
          >
            <LogOut size={20} />
            <span>Logout</span>
          </button>
        </motion.div>
      </div>
    </div>
  );
};

export default Dashboard;
